import { useCallback } from 'react';
import { useParams } from 'react-router';
import useFetch from './useFetch';

const useUploadAsset = (setUserError) => {
  const { domainId, accountId } = useParams();

  const fetchUrl = `/api/admin/accounts/${accountId}/domains/${domainId}/assets`;
  const documentsFetchUrl = `/api/admin/accounts/${accountId}/domains/${domainId}/assets/documents`;
  const fetch = useFetch();

  const uploadReferenceImage = useCallback(
    (asset, file, callback) => {
      if (!file) {
        setUserError('No file selected.');
        return;
      } else if (!/^image\/(jpeg|png|gif|bmp|webp|svg\+xml)$/.test(file.type)) {
        setUserError('File not an image.');
        return;
      } else if (file.size > 5242880) {
        // Maximum allowed size is 5MB (5242880 bytes)
        setUserError({ message: 'Image size is too big. Maximum allowed size is 5MB.' });
        return;
      }

      let fd = new FormData();
      fd.append('type', file.type);
      fd.append('image', file);
      fd.append('title', asset.title);
      fd.append('description', asset.description);

      fetch(fetchUrl, {
        method: 'POST',
        headers: {},
        body: fd,
      })
        .then((res) => {
          if (callback && typeof callback === 'function') {
            callback(res.asset);
          }
        })
        .catch((error) => {
          setUserError(error);
        });
    },
    [fetch, fetchUrl, setUserError]
  );

  // Resolves with the uploaded file's URL, or undefined when it failed (the error goes to setUserError as {message}).
  const uploadDocument = useCallback(
    (asset, file, callback) => {
      if (!file) {
        setUserError({ message: 'No file selected.' });
        return Promise.resolve();
      } else if (
        !/^(application\/pdf|application\/msword|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document|application\/vnd\.ms-excel|application\/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet|text\/plain)$/.test(
          file.type
        )
      ) {
        setUserError({ message: 'File is not a supported document format.' });
        return Promise.resolve();
      }

      let fd = new FormData();
      fd.append('type', file.type);
      fd.append('document', file);
      fd.append('title', asset.title);
      fd.append('description', asset.description);

      return fetch(documentsFetchUrl, {
        method: 'POST',
        headers: {},
        body: fd,
      })
        .then((res) => {
          if (callback && typeof callback === 'function') {
            callback(res.asset);
          }
          return res.asset;
        })
        .catch((error) => {
          setUserError({ message: error.message || 'Upload failed.' });
        });
    },
    [fetch, documentsFetchUrl, setUserError]
  );

  return { uploadReferenceImage, uploadDocument };
};

export default useUploadAsset;
